
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Drama } from '../component/Slider';
import { idbStorage } from './idbStorage';

//global loader
interface LoaderState {
    isLoading: boolean;
    showLoader: () => void;
    hideLoader: () => void;
}

export const useLoaderStore = create<LoaderState>((set) => ({
    isLoading: false,
    showLoader: () => set({ isLoading: true }),
    hideLoader: () => set({ isLoading: false }),
}));


//global toast notification
type ToastType = 'success' | 'error' | 'info';

interface ToastState {
    isOpen: boolean;
    message: string;
    type: ToastType;
    showToast: (message: string, type?: ToastType) => void;
    hideToast: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
    isOpen: false,
    message: '',
    type: 'info',
    showToast: (message, type = 'info') => set({ isOpen: true, message, type }),
    hideToast: () => set({ isOpen: false, message: '', type: 'info' }),
}));



// global social share modal.
interface ShareModalState {
    isOpen: boolean;
    drama: Drama | null;
    openShareModal: (drama: Drama) => void;
    closeShareModal: () => void;
}

export const useShareModalStore = create<ShareModalState>((set) => ({
    isOpen: false,
    drama: null,
    openShareModal: (drama) => set({ isOpen: true, drama }),
    closeShareModal: () => set({ isOpen: false, drama: null }),
}));

// unlock modal state for syncing between parent and child
interface UnlockModalState {
    isOpen: boolean;
    setModalOpen: (isOpen: boolean) => void;
}

export const useUnlockModalStore = create<UnlockModalState>((set) => ({
    isOpen: false,
    setModalOpen: (isOpen: boolean) => set({ isOpen }),
}));

//##################################################################################
//##################################################################################
//dont touch above stores


/**
 * Represents the viewing progress for a single episode.
 */
export interface EpisodeProgress {
    d_id: string; 
    d_image: string;
    d_title: string;
    e_id: string;
    epi_num: number;
    total_ep:number|string;
    duration: number; //left off time sec
    watchedOn: number; // Timestamp for sorting
    d_desc?: string; // Episode description
}

//The state shape for the watch progress store
interface WatchProgressState {
    // We use an object map for efficient lookups. The key is `${dramaId}-${episodeNum}`.
    progress: Record<string, EpisodeProgress>;
    //Adds or updates the progress for a specific episode.
    upsertEpisodeProgress: (episodeData: Omit<EpisodeProgress, 'watchedOn'>) => void;
    //Removes the progress for a specific episode
    removeEpisodeProgress: (dramaId: string, episodeNum: number) => void;
}

export const useWatchProgressStore = create<WatchProgressState>()(
    persist<WatchProgressState>(
        (set) => ({
            progress: {},
            upsertEpisodeProgress: (episodeData) =>
                set((state) => {
                    const key = `${episodeData.d_id}`;
                    return {
                        progress: {
                            ...state.progress,
                            [key]: { ...episodeData, watchedOn: Date.now() },
                        },
                    };
                }),
            removeEpisodeProgress: (dramaId) =>
                set((state) => {
                    const key = `${dramaId}`;
                    // eslint-disable-next-line
                    const { [key]: _removedEpisode, ...rest } = state.progress; // Destructure to remove the key
                    return { progress: rest };
                }),
        }), {
        name: 'drama-watch-progress', // A unique name for the storage item
        storage: createJSONStorage(() => idbStorage), //custom IndexedDB storage
    }
    )
);

// watch-duration rewards (daily): track active play seconds and daily claims
interface WatchDurationState {
    totalTodaySeconds: number; // accumulated active play seconds for today
    lastResetTs: number | null; // timestamp of last reset (start of day)
    claimed10Today: boolean; // whether 10-min reward claimed today
    claimed20Today: boolean; // whether 20-min reward claimed today
    // add active play seconds (will reset if day rolled over)
    addPlaySeconds: (seconds: number) => void;
    // claim helpers
    canClaim10: () => boolean;
    canClaim20: () => boolean;
    claim10: () => boolean; // returns true if claim succeeded and awarded coins
    claim20: () => boolean;
}

export const useWatchDurationStore = create<WatchDurationState>()(
    persist<WatchDurationState>(
        (set, get) => ({
            totalTodaySeconds: 0,
            lastResetTs: null,
            claimed10Today: false,
            claimed20Today: false,
            addPlaySeconds: (seconds: number) => {
                try {
                    // determine local start of today
                    const startOfToday = new Date();
                    startOfToday.setHours(0, 0, 0, 0);
                    const todayTs = startOfToday.getTime();

                    // reset if lastResetTs is older than today
                    if ((get().lastResetTs ?? 0) < todayTs) {
                        set({ totalTodaySeconds: 0, lastResetTs: todayTs, claimed10Today: false, claimed20Today: false });
                    }

                    // add seconds (round to nearest integer)
                    const add = Math.max(0, Math.floor(seconds));
                    if (add <= 0) return;
                    set((state) => ({ totalTodaySeconds: state.totalTodaySeconds + add }));
                } catch {
                    // no-op
                }
            },
            canClaim10: () => {
                const s = get();
                return !s.claimed10Today && s.totalTodaySeconds >= 60 * 10;
            },
            canClaim20: () => {
                const s = get();
                return !s.claimed20Today && s.totalTodaySeconds >= 60 * 20;
            },
            claim10: () => {
                const s = get();
                if (s.claimed10Today) return false;
                if (s.totalTodaySeconds < 60 * 10) return false;
                // award coins via coin store
                try {
                    // lazy import to avoid circular imports at module-eval time
                    // eslint-disable-next-line @typescript-eslint/no-require-imports
                    const { useCoinStore } = require('./coinstore');
                    const reward = useCoinStore.getState().watch10Reward || 5;
                    useCoinStore.getState().addCoins(reward);
                    // mark claimed
                    set({ claimed10Today: true });
                    // notify coin store to push local changes with task flag and coin amount
                    try { useCoinStore.getState().pushLocalChanges({ is10: 1, coins: reward }, 'add'); } catch { /* noop */ }
                    return true;
                } catch {
                    return false;
                }
            },
            claim20: () => {
                const s = get();
                if (s.claimed20Today) return false;
                if (s.totalTodaySeconds < 60 * 20) return false;
                try {
                    // eslint-disable-next-line @typescript-eslint/no-require-imports
                    const { useCoinStore } = require('./coinstore');
                    const reward = useCoinStore.getState().watch20Reward || 10;
                    set({ claimed20Today: true });
                    useCoinStore.getState().addCoins(reward);
                    try { useCoinStore.getState().pushLocalChanges({ is20: 1, coins: reward }, 'add'); } catch { /* noop */ }
                    return true;
                } catch {
                    return false;
                }
            },
        }),
        {
            name: 'DramaFlicks-watch-duration',
            storage: createJSONStorage(() => idbStorage),
        }
    )
);



// favorite drama.
export interface FavoriteDrama {
    d_id: string;
    d_image: string;
    d_title: string;
    total_ep: number | string;
    d_desc: string;
    mobile_dt?: string;
    createdAt?: number; // timestamp for sorting newest-first
}

interface FavoriteState {
    favorites: Record<string, FavoriteDrama>;
    addFavorite: (drama: FavoriteDrama) => void;
    removeFavorite: (dramaId: string) => void;
}

export const useFavoriteStore = create<FavoriteState>()(
    persist<FavoriteState>(
        (set) => ({
            favorites: {},
            addFavorite: (drama) =>{
                if (
                    !drama.d_id ||
                    drama.d_id === "0" ||
                    !drama.d_title ||
                    drama.d_title.trim() === "" ||
                    !drama.d_image ||
                    drama.d_image.trim() === ""
                ) {
                    return;
                }
                set((state) => ({
                    favorites: {
                        ...state.favorites,
                        [drama.d_id]: {
                            ...drama,
                            mobile_dt: drama.mobile_dt || new Date().toLocaleString('en-GB'),
                            createdAt: Date.now(),
                        },
                    },
                }))},
            removeFavorite: (dramaId) =>
                set((state) => {
                    // eslint-disable-next-line
                    const { [dramaId]: _removedEpisode, ...rest } = state.favorites;
                    return { favorites: rest };
                }),
        }),
        {
            name: 'drama-favorites',
            storage: createJSONStorage(() => idbStorage),
        }
    )
);

//#############################################################################
// Watch Now cache (in-memory for this tab/session)

export interface WatchDrama {
    d_id: string;
    d_image: string;
    d_name: string;
    gradient?: string;
    total_d_stream: string;
    type?: string;
    unlock_count?: string;
    epi_num?: string;
    id?: string;
    image_url?: string;
    drama_name?: string;
    d_desc: string;
    added_dt: string;
}

interface WatchNowState {
    dramas: WatchDrama[];
    currentPage: number;
    latestCount: number; // size of last fetch to decide if more pages exist
    setInitial: (dramas: WatchDrama[], page?: number, latestCount?: number) => void;
    append: (dramas: WatchDrama[]) => void;
    clear: () => void;
}

export const useWatchNowStore = create<WatchNowState>()((set, ) => ({
    dramas: [],
    currentPage: 1,
    latestCount: 0,
    setInitial: (dramas, page = 1, latestCount = dramas.length) => set({ dramas, currentPage: page, latestCount }),
    append: (newDramas) => set((state) => {
        // const existingIds = new Set(state.dramas.map(d => d.d_id));
        // const unique = newDramas.filter(d => !existingIds.has(d.d_id));
        // const merged = [...state.dramas, ...unique];
        const merged = [...state.dramas, ...newDramas];
        return {
            dramas: merged,
            currentPage: state.currentPage + 1,
            latestCount: newDramas.length,
        };
    }),
    clear: () => set({ dramas: [], currentPage: 1, latestCount: 0 }),
}));

// unlocked episodes store (persisted)
interface UnlockedState {
    // mapping: dramaId -> { [epiId]: timestamp }
    unlocked: Record<string, Record<string, number>>;
    unlockEpisode: (dramaId: string, epiId: string) => void;
    isUnlocked: (dramaId: string, epiId: string) => boolean;
    clearForDrama: (dramaId: string) => void;
}

export const useUnlockedStore = create<UnlockedState>()(
    persist<UnlockedState>(
        (set, get) => ({
            unlocked: {},
            unlockEpisode: (dramaId: string, epiId: string) => {
                set((state) => {
                    const dramamap = { ...(state.unlocked[dramaId] || {}) };
                    dramamap[epiId] = Date.now();
                    return { unlocked: { ...state.unlocked, [dramaId]: dramamap } };
                });
            },
            isUnlocked: (dramaId: string, epiId: string) => {
                const map = get().unlocked[dramaId];
                return !!(map && map[epiId]);
            },
            clearForDrama: (dramaId: string) => set((state) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [dramaId]: _rem, ...rest } = state.unlocked;
                return { unlocked: rest };
            }),
        }),
        {
            name: 'DramaFlicks-unlocked-episodes',
            storage: createJSONStorage(() => idbStorage),
        }
    )
);